import React, { useState } from "react";
import StatusBadge from "./StatusBadge";
import EmptyState from "./EmptyState";
import OnboardingStepBusinessDetails from "./onboarding/OnboardingStepBusinessDetails";
import OnboardingStepServices from "./onboarding/OnboardingStepServices";
import OnboardingStepBusinessConfig from "./onboarding/OnboardingStepBusinessConfig";
import OnboardingStepKnowledgeBase from "./onboarding/OnboardingStepKnowledgeBase";
import OnboardingStepTest from "./onboarding/OnboardingStepTest";
import OnboardingStepActivate from "./onboarding/OnboardingStepActivate";

const STEP_DEFS = [
  {
    key: "business_details",
    label: "Business Profile",
    description: "Verify business name, industry, and contact information.",
    required: true,
  },
  {
    key: "services",
    label: "Services",
    description: "Enable required AI services for this tenant.",
    required: true,
  },
  {
    key: "business_config",
    label: "Setup & Customization",
    description: "Configure AI tone, operating hours, and notification preferences.",
    required: true,
  },
  {
    key: "knowledge_base",
    label: "Knowledge Base",
    description: "Upload and index documents for Smart Document Search.",
    required: false,
  },
  {
    key: "test",
    label: "Test",
    description: "Validate service configuration with test interactions.",
    required: false,
  },
  {
    key: "activate",
    label: "Activate",
    description: "Activate the tenant and make services live.",
    required: true,
  },
];

const StepIcon = ({ index, status }) => {
  const isDone = status === "completed";
  const isActive = status === "in_progress";
  return (
    <span
      className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-xs font-bold ring-2 transition-colors ${
        isDone
          ? "bg-emerald-500 text-white ring-emerald-200"
          : isActive
          ? "bg-[#096dea] text-white ring-blue-200"
          : "bg-slate-100 text-slate-500 ring-slate-200"
      }`}
    >
      {isDone ? (
        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
        </svg>
      ) : (
        index + 1
      )}
    </span>
  );
};

const OnboardingWizard = ({
  onboarding,
  steps,
  tenant,
  tenantId,
  services,
  configs,
  onStart,
  onStepUpdate,
  onActivate,
  onToggleService,
  onConfigSave,
  isDisabled,
  loading,
}) => {
  const [activeIndex, setActiveIndex] = useState(0);

  if (!onboarding) {
    return (
      <EmptyState
        title="No onboarding state"
        description="Start onboarding for this tenant to track the required setup steps."
        actionLabel="Start onboarding"
        onAction={onStart}
      />
    );
  }

  const resolvedSteps = STEP_DEFS.map((def) => {
    const live = (steps || []).find((s) => s.step_key === def.key);
    return { ...def, status: live?.status || "pending" };
  });

  const activeStep = resolvedSteps[activeIndex];
  const isFirst = activeIndex === 0;
  const isLast = activeIndex === resolvedSteps.length - 1;
  // "Mark complete" is hidden on the Activate step — it has its own button
  const canMarkComplete =
    activeStep.status !== "completed" &&
    !isDisabled &&
    activeStep.key !== "activate";

  const handleMarkComplete = () => {
    let data_json = {};
    if (activeStep.key === "services") {
      const enabled = (services || []).filter((s) => s.status === "enabled");
      data_json = { selected_services: enabled.map((s) => s.service_key) };
    } else if (activeStep.key === "business_config") {
      const configMap = {};
      (configs || []).forEach((c) => {
        configMap[c.config_key] = c.config_json || {};
      });
      data_json = configMap;
    }
    onStepUpdate(activeStep.key, data_json);
  };

  const renderStepContent = () => {
    switch (activeStep.key) {
      case "business_details":
        return <OnboardingStepBusinessDetails tenant={tenant} />;
      case "services":
        return (
          <OnboardingStepServices
            tenantId={tenantId}
            services={services}
            onToggleService={onToggleService}
            disabled={isDisabled}
          />
        );
      case "business_config":
        return (
          <OnboardingStepBusinessConfig
            configs={configs}
            disabled={isDisabled}
            onSave={onConfigSave}
          />
        );
      case "knowledge_base":
        return (
          <OnboardingStepKnowledgeBase
            tenantId={tenantId}
            disabled={isDisabled}
          />
        );
      case "test":
        return <OnboardingStepTest tenantId={tenantId} />;
      case "activate":
        return (
          <OnboardingStepActivate
            tenant={tenant}
            services={services}
            configs={configs}
            steps={steps}
            onboarding={onboarding}
            onActivate={onActivate}
            isDisabled={isDisabled}
          />
        );
      default:
        return null;
    }
  };

  const progressPercent =
    onboarding.progress_percent ??
    Math.round(
      (resolvedSteps.filter((s) => s.status === "completed").length /
        resolvedSteps.length) *
        100
    );

  return (
    <div className="space-y-4">
      <section className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h3 className="text-base font-semibold text-slate-950">Onboarding wizard</h3>
            <p className="mt-0.5 text-sm text-slate-500">
              Complete required steps to activate this tenant.
            </p>
          </div>
          <div className="flex items-center gap-3">
            <StatusBadge status={onboarding.status} />
            <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-700">
              {progressPercent}% complete
            </span>
          </div>
        </div>

        <div className="mt-4">
          <div className="h-2 w-full overflow-hidden rounded-full bg-slate-100">
            <div
              className="h-full rounded-full bg-[#096dea] transition-all duration-500"
              style={{ width: `${progressPercent}%` }}
            />
          </div>
        </div>
      </section>

      <div className="flex flex-col gap-4 lg:flex-row lg:items-start">
        <aside className="w-full shrink-0 lg:w-64">
          <nav className="rounded-3xl border border-slate-200 bg-white p-3 shadow-sm">
            <ol className="space-y-1">
              {resolvedSteps.map((step, index) => {
                const isActive = index === activeIndex;
                return (
                  <li key={step.key}>
                    <button
                      type="button"
                      onClick={() => setActiveIndex(index)}
                      className={`flex w-full items-center gap-3 rounded-2xl px-3 py-3 text-left transition-colors ${
                        isActive ? "bg-[#eef5ff]" : "hover:bg-slate-50"
                      }`}
                    >
                      <StepIcon index={index} status={step.status} />
                      <div className="min-w-0 flex-1">
                        <p
                          className={`text-xs font-semibold leading-tight ${
                            isActive ? "text-[#096dea]" : "text-slate-700"
                          }`}
                        >
                          {step.label}
                        </p>
                        {step.required && step.status !== "completed" ? (
                          <span className="text-[10px] text-rose-400">Required</span>
                        ) : step.required ? null : (
                          <span className="text-[10px] text-slate-400">Optional</span>
                        )}
                      </div>
                      <StatusBadge
                        status={step.status}
                        className="hidden sm:inline-flex lg:hidden xl:inline-flex text-[9px] px-1.5 py-0.5"
                      />
                    </button>
                  </li>
                );
              })}
            </ol>
          </nav>

          <div className="mt-3 space-y-2">
            <button
              type="button"
              onClick={onStart}
              disabled={loading}
              className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-left text-xs font-semibold text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
            >
              ↺ Restart onboarding
            </button>
          </div>
        </aside>

        <div className="min-w-0 flex-1">
          <section className="rounded-3xl border border-slate-200 bg-white shadow-sm">
            <div className="flex items-start justify-between gap-4 border-b border-slate-100 px-6 py-5">
              <div className="flex items-center gap-3">
                <StepIcon index={activeIndex} status={activeStep.status} />
                <div>
                  <h4 className="text-sm font-semibold text-slate-950">{activeStep.label}</h4>
                  <p className="mt-0.5 text-xs text-slate-500">{activeStep.description}</p>
                </div>
              </div>
              <StatusBadge status={activeStep.status} />
            </div>

            <div className="p-6">{renderStepContent()}</div>

            <div className="flex items-center justify-between gap-3 border-t border-slate-100 px-6 py-4">
              <button
                type="button"
                onClick={() => setActiveIndex((i) => Math.max(0, i - 1))}
                disabled={isFirst}
                className="inline-flex items-center gap-1.5 rounded-xl border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40"
              >
                ← Previous
              </button>

              <div className="flex items-center gap-2">
                {canMarkComplete ? (
                  <button
                    type="button"
                    onClick={handleMarkComplete}
                    disabled={loading}
                    className="inline-flex items-center gap-1.5 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-2 text-sm font-semibold text-emerald-800 transition hover:bg-emerald-100 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    ✓ Mark complete
                  </button>
                ) : null}
                {!isLast ? (
                  <button
                    type="button"
                    onClick={() =>
                      setActiveIndex((i) => Math.min(resolvedSteps.length - 1, i + 1))
                    }
                    className="inline-flex items-center gap-1.5 rounded-xl bg-[#096dea] px-4 py-2 text-sm font-semibold text-white transition hover:bg-[#0959bb]"
                  >
                    Next →
                  </button>
                ) : null}
              </div>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default OnboardingWizard;
